import { DERIV_SYMBOL_RE } from './broker';
import type { DurationUnit } from './contracts';

/**
 * Traderoom: free Sobe/Desce entries outside the round clocks, traded as Deriv Rise/Fall
 * (CALL/PUT) on any asset. The contract starts when bought and lasts the chosen ticks (1–10, the
 * synthetic indices) or time (15 s and up on the synthetic indices; forex starts at 15 min) — each
 * asset's own limits come from contracts_for.
 */
export type ExpiryMode = 'time' | 'ticks';

export const FREE_EXPIRY_LIMITS = {
  ticks: { min: 1, max: 10 },
  /** Seconds. */
  time: { min: 15, max: 86_400 },
} as const;

/** Time presets offered in the order panel (seconds); each asset shows the ones inside its limits. */
export const FREE_TIME_PRESETS = [15, 30, 60, 120, 300, 900, 1800, 3600, 14_400, 86_400] as const;
export const FREE_DEFAULT_SECONDS = 60;
export const FREE_DEFAULT_TICKS = 5;

/** Candle widths offered on the Traderoom chart (seconds). 1 minute is the default. */
export const TRADEROOM_GRANULARITIES = [60, 300, 900, 1800] as const;
export type TraderoomGranularity = (typeof TRADEROOM_GRANULARITIES)[number];

/** Broker duration for an expiry: ticks as `t`; whole hours as `h`, whole minutes as `m`, the rest as `s`. */
export function freeDuration(mode: ExpiryMode, value: number): { duration: number; durationUnit: DurationUnit } {
  if (mode === 'ticks') return { duration: value, durationUnit: 't' };
  if (value % 3600 === 0) return { duration: value / 3600, durationUnit: 'h' };
  return value % 60 === 0 ? { duration: value / 60, durationUnit: 'm' } : { duration: value, durationUnit: 's' };
}

/** Contract length in seconds (null for tick contracts). */
export function durationSeconds(duration: number, unit: DurationUnit): number | null {
  switch (unit) {
    case 's':
      return duration;
    case 'm':
      return duration * 60;
    case 'h':
      return duration * 3600;
    case 'd':
      return duration * 86_400;
    default:
      return null;
  }
}

/** Error message for an expiry outside the Traderoom limits, or null when valid (Deriv still checks the asset's own). */
export function validateFreeDuration(duration: number, unit: DurationUnit): string | null {
  if (!Number.isInteger(duration) || duration < 1) return 'Expiração inválida.';
  if (unit === 't') return duration <= FREE_EXPIRY_LIMITS.ticks.max ? null : `Expiração de ${FREE_EXPIRY_LIMITS.ticks.min} a ${FREE_EXPIRY_LIMITS.ticks.max} ticks.`;
  const s = durationSeconds(duration, unit);
  return s !== null && s >= FREE_EXPIRY_LIMITS.time.min && s <= FREE_EXPIRY_LIMITS.time.max ? null : 'Expiração entre 15 segundos e 1 dia.';
}

/** Short pt-BR label: 5 ticks, 15s, 1 min, 1 h. */
export function expiryLabel(mode: ExpiryMode, value: number): string {
  if (mode === 'ticks') return `${value} tick${value === 1 ? '' : 's'}`;
  if (value < 60) return `${value}s`;
  if (value % 86_400 === 0) return `${value / 86_400} dia${value === 86_400 ? '' : 's'}`;
  if (value % 3600 === 0) return `${value / 3600} h`;
  const m = Math.floor(value / 60);
  const s = value % 60;
  return s ? `${m} min ${s}s` : `${m} min`;
}

/** Time presets inside an asset's limits (seconds). */
export function timePresetsWithin(limits: { min: number; max: number } | null): number[] {
  if (!limits) return [];
  return FREE_TIME_PRESETS.filter((p) => p >= limits.min && p <= limits.max);
}

// ---------------------------------------------------------------- assets

/** Traderoom assets: any Deriv symbol (1HZ100V, R_100, frxEURUSD, cryBTCUSD, OTC_AEX…). */
export function isTraderoomSymbol(symbol: string): boolean {
  return DERIV_SYMBOL_RE.test(symbol);
}

/**
 * Deriv's synthetic indices tick every 1–2 seconds around the clock; forex, crypto, stock indices
 * and commodities follow their markets (quiet minutes, closed hours).
 */
export function isSyntheticSymbol(symbol: string): boolean {
  return !/^(frx|cry|OTC_)/.test(symbol);
}

export type AssetCategory = 'synthetic' | 'forex' | 'crypto' | 'indices' | 'commodities';

/** Markets of the asset browser, in display order. */
export const ASSET_CATEGORIES: { key: AssetCategory; label: string }[] = [
  { key: 'synthetic', label: 'Sintéticos' },
  { key: 'forex', label: 'Forex' },
  { key: 'crypto', label: 'Cripto' },
  { key: 'indices', label: 'Índices' },
  { key: 'commodities', label: 'Commodities' },
];

/** Deriv market (active_symbols `market`) → market of the asset browser. */
export function categoryOfMarket(market: string): AssetCategory {
  switch (market) {
    case 'synthetic_index':
      return 'synthetic';
    case 'cryptocurrency':
      return 'crypto';
    case 'indices':
      return 'indices';
    case 'commodities':
      return 'commodities';
    default:
      return 'forex';
  }
}

/** Deriv submarkets, in pt-BR (the groups inside each market). */
const SUBMARKET_LABEL: Record<string, string> = {
  random_index: 'Volatilidade',
  crash_index: 'Crash e Boom',
  jump_index: 'Jump',
  step_index: 'Step',
  range_index: 'Range Break',
  random_daily: 'Mercado em alta e em baixa',
  forex_basket: 'Cestas de moedas',
  commodity_basket: 'Cesta de commodities',
  major_pairs: 'Pares principais',
  minor_pairs: 'Pares secundários',
  europe_OTC: 'Europa',
  americas_OTC: 'Américas',
  asia_oceania_OTC: 'Ásia e Oceania',
  metals: 'Metais',
  energy: 'Energia',
  non_stable_coin: 'Criptomoedas',
};

/** Group of an asset inside its market (Deriv's submarket). */
export function assetGroup(category: AssetCategory, submarket: string): { key: string; label: string } {
  return { key: `${category}:${submarket}`, label: SUBMARKET_LABEL[submarket] ?? ASSET_CATEGORIES.find((c) => c.key === category)?.label ?? submarket };
}

/** Group order: the market order, then the submarkets as listed above. */
export function assetGroupRank(key: string): number {
  const [category, submarket = ''] = key.split(':');
  const c = ASSET_CATEGORIES.findIndex((x) => x.key === category);
  const s = Object.keys(SUBMARKET_LABEL).indexOf(submarket);
  return (c < 0 ? 99 : c) * 100 + (s < 0 ? 99 : s);
}

/** Broker name → the name shown on screen. */
export function assetDisplayName(name: string): string {
  return name;
}

/** Expiries an asset offers in the Traderoom (Rise/Fall rows of contracts_for). */
export interface ExpiryLimits {
  symbol: string;
  /** Tick expiries (synthetic indices: 1–10), or null when the asset has none. */
  ticks: { min: number; max: number } | null;
  /** Time expiries in seconds (intraday), or null. */
  time: { min: number; max: number } | null;
}
